import * as THREE from "three";
import {
  FullScreenQuad,
  Pass,
} from "three/addons/postprocessing/Pass.js";
import type { SuntraceImageDepthProfile } from "./imageDepth";

const vertexShader = `
  varying vec2 vUv;

  void main() {
    vUv = uv;
    gl_Position = vec4(position.xy, 0.0, 1.0);
  }
`;

const maximumDarkeningCap = 0.18;
const maximumInscatteringCap = 0.16;
const surfaceDepthThreshold = 0.999999;

const clampCompositeLimit = (value: number, maximum: number): number =>
  THREE.MathUtils.clamp(Number.isFinite(value) ? value : maximum, 0, maximum);

export const evaluateSuntraceImageDepthContribution = (
  rawDepth: number,
  visibility: number,
  requestedInscattering: number,
  maximumDarkening = maximumDarkeningCap,
  maximumInscattering = maximumInscatteringCap,
): { contact: number; atmosphere: number } => {
  const surfaceDepthMask =
    Number.isFinite(rawDepth) && rawDepth < surfaceDepthThreshold ? 1 : 0;
  const safeVisibility = Number.isFinite(visibility) ? visibility : 1;
  const safeInscattering = Number.isFinite(requestedInscattering)
    ? requestedInscattering
    : 0;
  return {
    contact:
      THREE.MathUtils.clamp(
        1 - safeVisibility,
        0,
        clampCompositeLimit(maximumDarkening, maximumDarkeningCap),
      ) * surfaceDepthMask,
    atmosphere:
      THREE.MathUtils.clamp(
        safeInscattering,
        0,
        clampCompositeLimit(maximumInscattering, maximumInscatteringCap),
      ) * surfaceDepthMask,
  };
};

const gatherShader = `
  #define SUNTRACE_GTAO_SLICES 2
  #define SUNTRACE_GTAO_STEPS 4

  uniform sampler2D depthTexture;
  uniform mat4 inverseProjectionMatrix;
  uniform mat4 projectionMatrix;
  uniform float worldRadius;
  uniform float thickness;
  varying vec2 vUv;

  const float SUNTRACE_PI = 3.141592653589793;

  float interleavedGradientNoise(vec2 screenCoordinate) {
    return fract(52.9829189 * fract(dot(screenCoordinate, vec2(0.06711056, 0.00583715))));
  }

  vec3 reconstructViewPosition(vec2 uv, float rawDepth) {
    vec4 clipPosition = vec4(uv * 2.0 - 1.0, rawDepth * 2.0 - 1.0, 1.0);
    vec4 viewPosition = inverseProjectionMatrix * clipPosition;
    return viewPosition.xyz / viewPosition.w;
  }

  float gatherHorizon(
    vec2 sampleUv,
    vec3 centerViewPosition,
    vec3 viewDirection
  ) {
    if (any(lessThan(sampleUv, vec2(0.0))) || any(greaterThan(sampleUv, vec2(1.0)))) {
      return -1.0;
    }

    float sampleDepth = texture2D(depthTexture, sampleUv).x;
    if (sampleDepth >= 0.999999) {
      return -1.0;
    }

    vec3 sampleViewPosition = reconstructViewPosition(sampleUv, sampleDepth);
    vec3 delta = sampleViewPosition - centerViewPosition;
    float distanceToSample = max(length(delta), 0.0001);
    if (abs(delta.z) >= thickness) {
      return -1.0;
    }

    float falloff = clamp(1.0 - distanceToSample / max(worldRadius, 0.0001), 0.0, 1.0);
    float horizonCosine = dot(delta, viewDirection) / distanceToSample;
    return mix(-1.0, horizonCosine, falloff);
  }

  void main() {
    float centerDepth = texture2D(depthTexture, vUv).x;
    if (centerDepth >= 0.999999) {
      gl_FragColor = vec4(0.5, 0.5, 1.0, 1.0);
      return;
    }

    vec3 centerViewPosition = reconstructViewPosition(vUv, centerDepth);
    vec3 viewNormal = normalize(cross(dFdx(centerViewPosition), dFdy(centerViewPosition)));
    if (viewNormal.z < 0.0) {
      viewNormal = -viewNormal;
    }

    vec2 projectionScale = vec2(abs(projectionMatrix[0][0]), abs(projectionMatrix[1][1]));
    vec2 radiusUv = worldRadius * projectionScale / max(-centerViewPosition.z, 0.0001) * 0.5;
    radiusUv = clamp(radiusUv, vec2(0.004), vec2(0.08));
    float noise = interleavedGradientNoise(gl_FragCoord.xy);
    float visibility = 0.0;

    for (int sliceIndex = 0; sliceIndex < SUNTRACE_GTAO_SLICES; sliceIndex += 1) {
      float angle = (float(sliceIndex) / float(SUNTRACE_GTAO_SLICES) + noise) * SUNTRACE_PI;
      vec2 sliceDirection = vec2(cos(angle), sin(angle));
      vec3 positiveDirection = normalize(vec3(sliceDirection, 0.0));
      vec3 negativeDirection = -positiveDirection;
      float positiveHorizon = -1.0;
      float negativeHorizon = -1.0;

      for (int stepIndex = 0; stepIndex < SUNTRACE_GTAO_STEPS; stepIndex += 1) {
        float stepAmount = (float(stepIndex) + noise + 0.5) / float(SUNTRACE_GTAO_STEPS);
        vec2 stepUv = sliceDirection * radiusUv * stepAmount;
        positiveHorizon = max(
          positiveHorizon,
          gatherHorizon(vUv + stepUv, centerViewPosition, positiveDirection)
        );
        negativeHorizon = max(
          negativeHorizon,
          gatherHorizon(vUv - stepUv, centerViewPosition, negativeDirection)
        );
      }

      float positiveAngle = acos(clamp(positiveHorizon, -1.0, 1.0));
      float negativeAngle = acos(clamp(negativeHorizon, -1.0, 1.0));
      visibility += clamp((positiveAngle + negativeAngle) / SUNTRACE_PI, 0.0, 1.0);
    }

    visibility = pow(visibility / float(SUNTRACE_GTAO_SLICES), 1.6);
    gl_FragColor = vec4(viewNormal * 0.5 + 0.5, visibility);
  }
`;

const compositeShader = `
  uniform sampler2D sceneTexture;
  uniform sampler2D depthTexture;
  uniform sampler2D gatherTexture;
  uniform mat4 inverseProjectionMatrix;
  uniform mat4 cameraWorldMatrix;
  uniform mat4 projectionMatrix;
  uniform vec2 resolution;
  uniform vec2 gatherResolution;
  uniform vec3 fogColor;
  uniform vec3 environmentColor;
  uniform vec3 viewSunDirection;
  uniform float fogNear;
  uniform float fogFar;
  uniform float worldRadius;
  uniform float maximumDarkening;
  uniform float maximumInscattering;
  varying vec2 vUv;

  vec3 reconstructCompositeViewPosition(vec2 uv, float rawDepth) {
    vec4 clipPosition = vec4(uv * 2.0 - 1.0, rawDepth * 2.0 - 1.0, 1.0);
    vec4 viewPosition = inverseProjectionMatrix * clipPosition;
    return viewPosition.xyz / viewPosition.w;
  }

  vec3 decodeGatherNormal(vec2 uv) {
    return normalize(texture2D(gatherTexture, uv).rgb * 2.0 - 1.0);
  }

  float suntraceBilateralWeight(
    vec2 sampleUv,
    float centerViewZ,
    vec3 centerNormal
  ) {
    float sampleDepth = texture2D(depthTexture, sampleUv).x;
    if (sampleDepth >= 0.999999) {
      return 0.0;
    }

    float sampleViewZ = reconstructCompositeViewPosition(sampleUv, sampleDepth).z;
    float depthScale = max(abs(centerViewZ) * 0.006, 0.025);
    float depthWeight = exp(-abs(sampleViewZ - centerViewZ) / depthScale);
    float normalAgreement = max(dot(centerNormal, decodeGatherNormal(sampleUv)), 0.0);
    return depthWeight * mix(0.15, 1.0, normalAgreement * normalAgreement);
  }

  void suntraceAccumulateBilateral(
    vec2 sampleUv,
    float centerViewZ,
    vec3 centerNormal,
    inout float visibilitySum,
    inout float weightSum
  ) {
    float weight = suntraceBilateralWeight(sampleUv, centerViewZ, centerNormal);
    visibilitySum += texture2D(gatherTexture, sampleUv).a * weight;
    weightSum += weight;
  }

  float suntraceEightTapBilateral(
    vec2 uv,
    float centerViewZ,
    vec3 centerNormal
  ) {
    vec2 texel = vec2(1.0 / gatherResolution.x, 1.0 / gatherResolution.y);
    float visibilitySum = 0.0;
    float weightSum = 0.0;

    suntraceAccumulateBilateral(uv + vec2(-texel.x, 0.0), centerViewZ, centerNormal, visibilitySum, weightSum);
    suntraceAccumulateBilateral(uv + vec2(texel.x, 0.0), centerViewZ, centerNormal, visibilitySum, weightSum);
    suntraceAccumulateBilateral(uv + vec2(0.0, -texel.y), centerViewZ, centerNormal, visibilitySum, weightSum);
    suntraceAccumulateBilateral(uv + vec2(0.0, texel.y), centerViewZ, centerNormal, visibilitySum, weightSum);
    suntraceAccumulateBilateral(uv + vec2(-texel.x, -texel.y), centerViewZ, centerNormal, visibilitySum, weightSum);
    suntraceAccumulateBilateral(uv + vec2(texel.x, -texel.y), centerViewZ, centerNormal, visibilitySum, weightSum);
    suntraceAccumulateBilateral(uv + vec2(-texel.x, texel.y), centerViewZ, centerNormal, visibilitySum, weightSum);
    suntraceAccumulateBilateral(uv + vec2(texel.x, texel.y), centerViewZ, centerNormal, visibilitySum, weightSum);

    float centerVisibility = texture2D(gatherTexture, uv).a;
    return weightSum > 0.01 ? visibilitySum / weightSum : centerVisibility;
  }

  float suntraceLuminance(vec3 color) {
    return dot(color, vec3(0.2126, 0.7152, 0.0722));
  }

  void main() {
    vec4 sceneColor = texture2D(sceneTexture, vUv);
    float rawDepth = texture2D(depthTexture, vUv).x;
    float surfaceDepthMask = 1.0 - step(${surfaceDepthThreshold.toFixed(6)}, rawDepth);
    if (surfaceDepthMask <= 0.0) {
      gl_FragColor = sceneColor;
      return;
    }

    vec3 viewPosition = reconstructCompositeViewPosition(vUv, rawDepth);
    vec3 centerNormal = decodeGatherNormal(vUv);
    float visibility = suntraceEightTapBilateral(vUv, viewPosition.z, centerNormal);
    vec2 projectionScale = vec2(abs(projectionMatrix[0][0]), abs(projectionMatrix[1][1]));
    vec2 projectedRadiusPixels = worldRadius * projectionScale * resolution * 0.5 /
      max(-viewPosition.z, 0.0001);
    float subpixelFade = smoothstep(0.75, 1.75, min(projectedRadiusPixels.x, projectedRadiusPixels.y));
    visibility = mix(1.0, clamp(visibility, 0.0, 1.0), subpixelFade);

    float sceneLuminance = suntraceLuminance(sceneColor.rgb);
    float environmentLuminance = max(suntraceLuminance(environmentColor), 0.0001);
    vec3 environmentChromaticity = environmentColor / environmentLuminance;
    float sunFacing = max(dot(centerNormal, normalize(viewSunDirection)), 0.0);
    float ambientLuminance = min(
      sceneLuminance,
      mix(environmentLuminance, sceneLuminance, 0.35) * mix(0.72, 0.52, sunFacing)
    );
    vec3 ambientEstimate = min(
      sceneColor.rgb,
      max(environmentChromaticity * ambientLuminance, vec3(0.0))
    );
    vec3 directResidual = max(sceneColor.rgb - ambientEstimate, vec3(0.0));
    float boundedVisibility = max(visibility, 1.0 - maximumDarkening);
    vec3 contactColor = directResidual + ambientEstimate * boundedVisibility;
    contactColor = max(contactColor, sceneColor.rgb * (1.0 - maximumDarkening));

    vec3 worldPosition = (cameraWorldMatrix * vec4(viewPosition, 1.0)).xyz;
    float surfaceDistance = length(viewPosition);
    float fogRange = max(fogFar - fogNear, 0.0001);
    float normalizedSegment = clamp((surfaceDistance - fogNear) / fogRange, 0.0, 1.0);
    float heightTerm = mix(1.2, 0.72, smoothstep(-4.0, 42.0, worldPosition.y));
    float segmentTransmittance = exp(-2.0 * normalizedSegment * heightTerm);
    float relativeDepthGradient = fwidth(max(-viewPosition.z, 0.0001)) /
      max(-viewPosition.z, 0.0001);
    float shallowDepthGradientFade = mix(
      0.4,
      1.0,
      smoothstep(0.00035, 0.0025, relativeDepthGradient)
    );
    vec3 viewDirection = normalize(-viewPosition);
    float sunPhase = pow(max(dot(viewDirection, normalize(viewSunDirection)), 0.0), 4.0);
    float aerialWeight = (1.0 - segmentTransmittance) *
      shallowDepthGradientFade * mix(0.88, 1.0, sunPhase) * surfaceDepthMask;
    aerialWeight = clamp(aerialWeight, 0.0, maximumInscattering);

    vec3 compositeColor = mix(contactColor, fogColor, aerialWeight);
    gl_FragColor = vec4(compositeColor, sceneColor.a);
  }
`;

export const getSuntraceImageDepthShaderContract = (): {
  gather: string;
  composite: string;
} => ({ gather: gatherShader, composite: compositeShader });

type GatherUniforms = {
  depthTexture: THREE.IUniform<THREE.DepthTexture | null>;
  inverseProjectionMatrix: THREE.IUniform<THREE.Matrix4>;
  projectionMatrix: THREE.IUniform<THREE.Matrix4>;
  worldRadius: THREE.IUniform<number>;
  thickness: THREE.IUniform<number>;
};

type CompositeUniforms = {
  sceneTexture: THREE.IUniform<THREE.Texture | null>;
  depthTexture: THREE.IUniform<THREE.DepthTexture | null>;
  gatherTexture: THREE.IUniform<THREE.Texture>;
  inverseProjectionMatrix: THREE.IUniform<THREE.Matrix4>;
  cameraWorldMatrix: THREE.IUniform<THREE.Matrix4>;
  projectionMatrix: THREE.IUniform<THREE.Matrix4>;
  resolution: THREE.IUniform<THREE.Vector2>;
  gatherResolution: THREE.IUniform<THREE.Vector2>;
  fogColor: THREE.IUniform<THREE.Color>;
  environmentColor: THREE.IUniform<THREE.Color>;
  viewSunDirection: THREE.IUniform<THREE.Vector3>;
  fogNear: THREE.IUniform<number>;
  fogFar: THREE.IUniform<number>;
  worldRadius: THREE.IUniform<number>;
  maximumDarkening: THREE.IUniform<number>;
  maximumInscattering: THREE.IUniform<number>;
};

export type SuntraceImageDepthAtmosphere = {
  fogColor: THREE.Color;
  environmentColor: THREE.Color;
  fogNear: number;
  fogFar: number;
  sunDirection: THREE.Vector3;
};

export type SuntraceImageDepthCompositeState = SuntraceImageDepthAtmosphere & {
  maximumDarkening: number;
  maximumInscattering: number;
};

export class SuntraceImageDepthPass extends Pass {
  private readonly camera: THREE.Camera;
  private readonly target: THREE.WebGLRenderTarget;
  private readonly gatherUniforms: GatherUniforms;
  private readonly compositeUniforms: CompositeUniforms;
  private readonly gatherMaterial: THREE.ShaderMaterial;
  private readonly compositeMaterial: THREE.ShaderMaterial;
  private readonly gatherQuad: FullScreenQuad;
  private readonly compositeQuad: FullScreenQuad;
  private readonly worldSunDirection = new THREE.Vector3(0.82, 0.23, -0.51).normalize();
  private disposed = false;

  constructor(camera: THREE.Camera, profile: SuntraceImageDepthProfile) {
    super();
    this.camera = camera;
    this.needsSwap = true;
    this.enabled = profile.enabled;
    this.target = new THREE.WebGLRenderTarget(1, 1, {
      type: THREE.HalfFloatType,
      format: THREE.RGBAFormat,
      minFilter: THREE.NearestFilter,
      magFilter: THREE.NearestFilter,
      generateMipmaps: false,
      depthBuffer: false,
      stencilBuffer: false,
    });
    this.target.texture.name = "SUNTRACE.GTAO.Gather";
    this.gatherUniforms = {
      depthTexture: { value: null },
      inverseProjectionMatrix: { value: this.camera.projectionMatrixInverse },
      projectionMatrix: { value: this.camera.projectionMatrix },
      worldRadius: { value: 0.62 },
      thickness: { value: 0.35 },
    };
    this.compositeUniforms = {
      sceneTexture: { value: null },
      depthTexture: { value: null },
      gatherTexture: { value: this.target.texture },
      inverseProjectionMatrix: { value: this.camera.projectionMatrixInverse },
      cameraWorldMatrix: { value: this.camera.matrixWorld },
      projectionMatrix: { value: this.camera.projectionMatrix },
      resolution: { value: new THREE.Vector2(1, 1) },
      gatherResolution: { value: new THREE.Vector2(1, 1) },
      fogColor: { value: new THREE.Color(0x72868c) },
      environmentColor: { value: new THREE.Color(0x7898a6) },
      viewSunDirection: { value: this.worldSunDirection.clone() },
      fogNear: { value: 90 },
      fogFar: { value: 700 },
      worldRadius: { value: 0.62 },
      maximumDarkening: {
        value: clampCompositeLimit(profile.maximumDarkening, maximumDarkeningCap),
      },
      maximumInscattering: {
        value: clampCompositeLimit(
          profile.maximumInscattering,
          maximumInscatteringCap,
        ),
      },
    };
    this.gatherMaterial = new THREE.ShaderMaterial({
      name: "SUNTRACE.GTAO.GatherMaterial",
      uniforms: this.gatherUniforms,
      vertexShader,
      fragmentShader: gatherShader,
      depthTest: false,
      depthWrite: false,
      blending: THREE.NoBlending,
      toneMapped: false,
    });
    this.compositeMaterial = new THREE.ShaderMaterial({
      name: "SUNTRACE.GTAO.CompositeMaterial",
      uniforms: this.compositeUniforms,
      vertexShader,
      fragmentShader: compositeShader,
      depthTest: false,
      depthWrite: false,
      blending: THREE.NoBlending,
      toneMapped: false,
    });
    this.gatherQuad = new FullScreenQuad(this.gatherMaterial);
    this.compositeQuad = new FullScreenQuad(this.compositeMaterial);
  }

  get outputTexture(): THREE.Texture {
    return this.target.texture;
  }

  get compositeState(): SuntraceImageDepthCompositeState {
    return {
      fogColor: this.compositeUniforms.fogColor.value.clone(),
      environmentColor: this.compositeUniforms.environmentColor.value.clone(),
      fogNear: this.compositeUniforms.fogNear.value,
      fogFar: this.compositeUniforms.fogFar.value,
      sunDirection: this.worldSunDirection.clone(),
      maximumDarkening: this.compositeUniforms.maximumDarkening.value,
      maximumInscattering: this.compositeUniforms.maximumInscattering.value,
    };
  }

  setProfile(profile: SuntraceImageDepthProfile): void {
    this.enabled = profile.enabled;
    this.compositeUniforms.maximumDarkening.value = clampCompositeLimit(
      profile.maximumDarkening,
      maximumDarkeningCap,
    );
    this.compositeUniforms.maximumInscattering.value = clampCompositeLimit(
      profile.maximumInscattering,
      maximumInscatteringCap,
    );
  }

  setAtmosphere(atmosphere: SuntraceImageDepthAtmosphere): void {
    this.compositeUniforms.fogColor.value.copy(atmosphere.fogColor);
    this.compositeUniforms.environmentColor.value.copy(atmosphere.environmentColor);
    this.compositeUniforms.fogNear.value = Number.isFinite(atmosphere.fogNear)
      ? Math.max(0, atmosphere.fogNear)
      : this.compositeUniforms.fogNear.value;
    const fogFar = Number.isFinite(atmosphere.fogFar)
      ? atmosphere.fogFar
      : this.compositeUniforms.fogFar.value;
    this.compositeUniforms.fogFar.value = Math.max(
      this.compositeUniforms.fogNear.value + 0.0001,
      fogFar,
    );
    const sunLengthSquared = atmosphere.sunDirection.lengthSq();
    if (Number.isFinite(sunLengthSquared) && sunLengthSquared > 0) {
      this.worldSunDirection.copy(atmosphere.sunDirection).normalize();
    }
  }

  override setSize(width: number, height: number): void {
    this.target.setSize(
      Math.max(1, Math.ceil(width * 0.5)),
      Math.max(1, Math.ceil(height * 0.5)),
    );
  }

  override render(
    renderer: THREE.WebGLRenderer,
    writeBuffer: THREE.WebGLRenderTarget,
    readBuffer: THREE.WebGLRenderTarget,
  ): void {
    if (readBuffer.depthTexture === null) {
      throw new Error("SUNTRACE image depth pass requires a composer depth texture");
    }

    this.gatherUniforms.depthTexture.value = readBuffer.depthTexture;
    this.gatherUniforms.inverseProjectionMatrix.value = this.camera.projectionMatrixInverse;
    this.gatherUniforms.projectionMatrix.value = this.camera.projectionMatrix;
    this.compositeUniforms.sceneTexture.value = readBuffer.texture;
    this.compositeUniforms.depthTexture.value = readBuffer.depthTexture;
    this.compositeUniforms.inverseProjectionMatrix.value = this.camera.projectionMatrixInverse;
    this.compositeUniforms.cameraWorldMatrix.value = this.camera.matrixWorld;
    this.compositeUniforms.projectionMatrix.value = this.camera.projectionMatrix;
    this.compositeUniforms.resolution.value.set(readBuffer.width, readBuffer.height);
    this.compositeUniforms.gatherResolution.value.set(this.target.width, this.target.height);
    this.compositeUniforms.viewSunDirection.value
      .copy(this.worldSunDirection)
      .transformDirection(this.camera.matrixWorldInverse);

    const renderTarget = renderer.getRenderTarget();
    const viewport = renderer.getViewport(new THREE.Vector4());
    const scissor = renderer.getScissor(new THREE.Vector4());
    const scissorTest = renderer.getScissorTest();
    const clearColor = renderer.getClearColor(new THREE.Color());
    const clearAlpha = renderer.getClearAlpha();
    const autoClear = renderer.autoClear;

    try {
      renderer.setRenderTarget(this.target);
      renderer.setViewport(0, 0, this.target.width, this.target.height);
      renderer.setScissor(0, 0, this.target.width, this.target.height);
      renderer.setScissorTest(false);
      renderer.setClearColor(0x8080ff, 1);
      renderer.autoClear = false;
      renderer.clear(true, false, false);
      this.gatherQuad.render(renderer);
      renderer.setRenderTarget(this.renderToScreen ? null : writeBuffer);
      renderer.setViewport(viewport);
      renderer.setScissor(scissor);
      renderer.setScissorTest(scissorTest);
      renderer.setClearColor(clearColor, clearAlpha);
      renderer.autoClear = autoClear;
      if (this.clear) {
        renderer.clear(renderer.autoClearColor, renderer.autoClearDepth, renderer.autoClearStencil);
      }
      this.compositeQuad.render(renderer);
    } finally {
      renderer.setRenderTarget(renderTarget);
      renderer.setViewport(viewport);
      renderer.setScissor(scissor);
      renderer.setScissorTest(scissorTest);
      renderer.setClearColor(clearColor, clearAlpha);
      renderer.autoClear = autoClear;
    }
  }

  override dispose(): void {
    if (this.disposed) {
      return;
    }
    this.disposed = true;
    this.target.dispose();
    this.gatherMaterial.dispose();
    this.compositeMaterial.dispose();
    this.gatherQuad.dispose();
    this.compositeQuad.dispose();
  }
}
